import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
  Rating,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Divider,
  Stack,
  CircularProgress,
} from '@mui/material';
import {
  PlayArrow,
  School,
  AccessTime,
  Star,
  Person,
  CheckCircle,
  ExpandMore,
  Language,
  EmojiEvents,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../hooks/usePageTitle';

interface CourseDetail {
  id: number;
  title: string;
  description: string;
  longDescription: string;
  instructor: {
    name: string;
    title: string;
    bio: string;
    avatar: string;
  };
  duration: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  price: number;
  rating: number;
  reviewCount: number;
  students: number;
  category: string;
  tags: string[];
  chapters: {
    id: number;
    title: string;
    duration: string;
    lessons: {
      id: number;
      title: string;
      duration: string;
      type: 'video' | 'reading' | 'quiz';
    }[];
  }[];
  requirements: string[];
  outcomes: string[];
}

const CourseDetailPage: React.FC = () => {
  const { t } = useTranslation();
  
  // 设置页面标题
  usePageTitle('courseDetail');
  
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState<CourseDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [enrolled, setEnrolled] = useState(false);

  useEffect(() => {
    fetchCourseDetail();
  }, [courseId]);

  const fetchCourseDetail = async () => {
    setLoading(true);
    try {
      // 模拟API调用
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const mockCourse: CourseDetail = {
        id: parseInt(courseId || '1'),
        title: courseId === '1' ? 'Web3.0基础与区块链技术' : 
               courseId === '2' ? '人工智能与机器学习导论' : 
               '智能合约开发实战',
        description: '深入学习区块链技术基础、智能合约开发和去中心化应用构建',
        longDescription: '本课程将带您从零开始深入了解Web3.0和区块链技术的核心概念。您将学习比特币、以太坊的工作原理，掌握智能合约开发，并构建自己的去中心化应用（DApp）。课程结合理论与实践，通过大量的动手项目，让您真正掌握区块链开发技能。',
        instructor: {
          name: 'Dr. Bob Chen',
          title: '区块链技术专家',
          bio: '前以太坊基金会技术顾问，拥有10年区块链开发经验，发表论文30余篇',
          avatar: '/avatar-placeholder.jpg',
        },
        duration: '40小时',
        difficulty: 'beginner',
        price: 299.99,
        rating: 4.8,
        reviewCount: 156,
        students: 1250,
        category: '区块链',
        tags: ['Web3.0', '区块链', '智能合约', 'DeFi', 'NFT'],
        chapters: [
          {
            id: 1,
            title: '区块链基础概念',
            duration: '8小时',
            lessons: [
              { id: 1, title: '什么是区块链', duration: '20分钟', type: 'video' },
              { id: 2, title: '去中心化的原理', duration: '25分钟', type: 'video' },
              { id: 3, title: '加密学基础', duration: '30分钟', type: 'reading' },
              { id: 4, title: '章节测试', duration: '15分钟', type: 'quiz' },
            ],
          },
          {
            id: 2,
            title: '以太坊平台深入',
            duration: '12小时',
            lessons: [
              { id: 5, title: '以太坊虚拟机', duration: '35分钟', type: 'video' },
              { id: 6, title: 'Gas费机制', duration: '25分钟', type: 'video' },
              { id: 7, title: '智能合约概念', duration: '40分钟', type: 'video' },
              { id: 8, title: '实践练习', duration: '30分钟', type: 'quiz' },
            ],
          },
          {
            id: 3,
            title: '智能合约开发',
            duration: '15小时',
            lessons: [
              { id: 9, title: 'Solidity语言基础', duration: '45分钟', type: 'video' },
              { id: 10, title: '合约部署与测试', duration: '50分钟', type: 'video' },
              { id: 11, title: '项目实战', duration: '60分钟', type: 'video' },
            ],
          },
          {
            id: 4,
            title: 'DApp开发实战',
            duration: '5小时',
            lessons: [
              { id: 12, title: '前端集成', duration: '40分钟', type: 'video' },
              { id: 13, title: '最终项目', duration: '120分钟', type: 'video' },
            ],
          },
        ],
        requirements: [
          '基础编程知识（JavaScript或Python）',
          '了解基本的网络概念',
          '对区块链技术有基本兴趣',
        ],
        outcomes: [
          '理解区块链技术的核心原理',
          '能够开发和部署智能合约',
          '掌握DApp开发流程',
          '获得Web3.0开发实战经验',
          '获得区块链认证证书',
        ],
      };
      
      setCourse(mockCourse);
      setEnrolled(Math.random() > 0.5); // 模拟注册状态
    } catch (error) {
      console.error('获取课程详情失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDifficultyLabel = (difficulty: string) => {
    const labels: { [key: string]: string } = {
      beginner: '初级',
      intermediate: '中级',
      advanced: '高级',
    };
    return labels[difficulty] || difficulty;
  };

  const getDifficultyColor = (difficulty: string) => {
    const colors: { [key: string]: 'success' | 'warning' | 'error' } = {
      beginner: 'success',
      intermediate: 'warning',
      advanced: 'error',
    };
    return colors[difficulty] || 'success';
  };

  const getLessonIcon = (type: string) => {
    switch (type) {
      case 'video':
        return <PlayArrow />;
      case 'reading':
        return <School />;
      case 'quiz':
        return <CheckCircle />;
      default:
        return <PlayArrow />;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          加载课程详情...
        </Typography>
      </Container>
    );
  }

  if (!course) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h6">课程未找到</Typography>
      </Container>
    );
  }

  return (
    <Box>
      {/* Course Header */}
      <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={8}>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Chip
                  label={getDifficultyLabel(course.difficulty)}
                  color={getDifficultyColor(course.difficulty)}
                  size="small"
                />
                <Chip label={course.category} variant="outlined" sx={{ color: 'white', borderColor: 'white' }} />
              </Stack>
              
              <Typography variant="h3" component="h1" gutterBottom>
                {course.title}
              </Typography>
              
              <Typography variant="h6" sx={{ mb: 3, opacity: 0.9 }}>
                {course.description}
              </Typography>
              
              <Stack direction="row" spacing={3} sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Rating value={course.rating} precision={0.1} readOnly />
                  <Typography variant="body2">
                    {course.rating} ({course.reviewCount} 评价)
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Person fontSize="small" />
                  <Typography variant="body2">
                    {course.students} 学生
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccessTime fontSize="small" />
                  <Typography variant="body2">
                    {course.duration}
                  </Typography>
                </Box>
              </Stack>
              
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {course.tags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    sx={{ color: 'white', borderColor: 'white' }}
                    variant="outlined"
                  />
                ))}
              </Stack>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h4" color="primary.main" gutterBottom>
                    ¥{course.price}
                  </Typography>
                  
                  {enrolled ? (
                    <Button
                      variant="contained"
                      fullWidth
                      size="large"
                      startIcon={<PlayArrow />}
                      sx={{ mb: 2 }}
                    >
                      继续学习
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      fullWidth
                      size="large"
                      sx={{ mb: 2 }}
                    >
                      立即注册
                    </Button>
                  )}
                  
                  <Button
                    variant="outlined"
                    fullWidth
                    size="large"
                    sx={{ mb: 3 }}
                  >
                    免费试听
                  </Button>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <List dense>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        <AccessTime />
                      </ListItemIcon>
                      <ListItemText primary={`总时长: ${course.duration}`} />
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        <Language />
                      </ListItemIcon>
                      <ListItemText primary="语言: 中文" />
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        <EmojiEvents />
                      </ListItemIcon>
                      <ListItemText primary="完成后获得NFT证书" />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            {/* Course Description */}
            <Paper sx={{ p: 3, mb: 4 }}>
              <Typography variant="h5" gutterBottom>
                课程介绍
              </Typography>
              <Typography variant="body1" paragraph>
                {course.longDescription}
              </Typography>
            </Paper>

            {/* Course Content */}
            <Paper sx={{ p: 3, mb: 4 }}>
              <Typography variant="h5" gutterBottom>
                课程内容
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {course.chapters.length} 个章节 • 总时长 {course.duration}
              </Typography>
              
              {course.chapters.map((chapter) => (
                <Accordion key={chapter.id}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6">{chapter.title}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {chapter.lessons.length} 节课 • {chapter.duration}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <List>
                      {chapter.lessons.map((lesson) => (
                        <ListItem key={lesson.id} sx={{ py: 1 }}>
                          <ListItemIcon>
                            {getLessonIcon(lesson.type)}
                          </ListItemIcon>
                          <ListItemText
                            primary={lesson.title}
                            secondary={lesson.duration}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Paper>

            {/* Requirements & Outcomes */}
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    学习要求
                  </Typography>
                  <List>
                    {course.requirements.map((req, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ListItemIcon>
                          <CheckCircle color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={req} />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    学习成果
                  </Typography>
                  <List>
                    {course.outcomes.map((outcome, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ListItemIcon>
                          <EmojiEvents color="success" />
                        </ListItemIcon>
                        <ListItemText primary={outcome} />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
            </Grid>
          </Grid>

          {/* Instructor Info */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                讲师介绍
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Avatar sx={{ width: 64, height: 64 }}>
                  {course.instructor.name.charAt(0)}
                </Avatar>
                <Box>
                  <Typography variant="h6">{course.instructor.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {course.instructor.title}
                  </Typography>
                </Box>
              </Box>
              
              <Typography variant="body2" paragraph>
                {course.instructor.bio}
              </Typography>
              
              <Button variant="outlined" fullWidth>
                查看讲师资料
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default CourseDetailPage; 